/**
 * DiscountSection — cupom / gift card inputs during checkout.
 *
 * UX rules:
 *  - Collapsed by default behind a discreet link ("Tenho um cupom ou gift
 *    card") so the checkout stays visually calm for buyers who don't have
 *    a code. Expands inline on click.
 *  - Coupon and gift card are MUTUALLY EXCLUSIVE. Applying one automatically
 *    removes the other on the backend; the inactive input is disabled in the
 *    UI for clarity.
 *  - Input and "Aplicar" button share ONE row (input 80% / button 20%).
 *  - Copy is sentence-case ("Inserir o código aqui"), no ALL-CAPS placeholders.
 *  - All money math lives on the backend.
 */
import { useState } from "react";
import { api, formatBRL } from "../lib/api";

export default function DiscountSection({ intentId, totals, onTotalsChange }) {
  const hasCoupon = Boolean(totals?.coupon_code);
  const hasGc = Boolean(totals?.giftcard_code);
  // Auto-expand when any code is already applied so buyers see what's active.
  const [expanded, setExpanded] = useState(hasCoupon || hasGc);
  const [couponInput, setCouponInput] = useState("");
  const [gcInput, setGcInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [gcError, setGcError] = useState("");

  const applyCoupon = async (e) => {
    e.preventDefault();
    setCouponError(""); setGcError("");
    if (!couponInput.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/mp/checkout/${intentId}/apply-discount`, {
        coupon_code: couponInput.trim(), gift_card_code: null,
      });
      onTotalsChange(r.data);
      setCouponInput(""); setGcInput("");
    } catch (err) {
      setCouponError(err?.response?.data?.detail || "Cupom inválido");
    } finally { setBusy(false); }
  };

  const applyGc = async (e) => {
    e.preventDefault();
    setGcError(""); setCouponError("");
    if (!gcInput.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/mp/checkout/${intentId}/apply-discount`, {
        coupon_code: null, gift_card_code: gcInput.trim(),
      });
      onTotalsChange(r.data);
      setGcInput(""); setCouponInput("");
    } catch (err) {
      setGcError(err?.response?.data?.detail || "Gift card inválido");
    } finally { setBusy(false); }
  };

  const removeAll = async () => {
    setBusy(true); setCouponError(""); setGcError("");
    try {
      const r = await api.post(`/mp/checkout/${intentId}/apply-discount`, {
        coupon_code: null, gift_card_code: null,
      });
      onTotalsChange(r.data);
    } catch { /* silent — user can retry */ }
    finally { setBusy(false); }
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-sm text-[#8C7B5E] hover:text-[#1A1A1A] underline underline-offset-4 transition-colors"
        data-testid="discount-toggle"
      >
        Tenho um cupom ou gift card
      </button>
    );
  }

  return (
    <div className="space-y-4" data-testid="discount-section">
      {/* Coupon row */}
      <div>
        <label className="text-xs text-[#6B6B6B] mb-1.5 block">
          Cupom de desconto
        </label>
        {hasCoupon ? (
          <div
            className="flex items-center justify-between px-3 py-2.5 border border-[#B5D0A0] bg-[#F1F7EC] text-sm"
            data-testid="coupon-applied"
          >
            <span className="text-[#3E6A1E] leading-tight">
              <strong>{totals.coupon_code}</strong> aplicado —{" "}
              −{formatBRL(totals.coupon_discount)}
            </span>
            <button
              type="button"
              onClick={removeAll}
              disabled={busy}
              className="text-xs text-[#6B6B6B] hover:text-[#1A1A1A] underline underline-offset-4"
              data-testid="coupon-remove-btn"
            >
              Remover
            </button>
          </div>
        ) : (
          <form onSubmit={applyCoupon} className="flex gap-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Inserir o código aqui"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              disabled={hasGc || busy}
              data-testid="coupon-input"
              style={{ textTransform: "uppercase" }}
            />
            <button
              type="submit"
              disabled={hasGc || busy || !couponInput.trim()}
              className="px-4 text-xs uppercase tracking-wider border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              data-testid="coupon-apply-btn"
              style={{ width: "22%" }}
            >
              Aplicar
            </button>
          </form>
        )}
        {couponError && (
          <p className="text-xs text-[#A8503B] mt-1.5" data-testid="coupon-error">
            {couponError}
          </p>
        )}
        {hasGc && !hasCoupon && (
          <p className="text-xs text-[#A8A8A8] mt-1.5">
            Remova o gift card para usar um cupom.
          </p>
        )}
      </div>

      {/* Gift card row — visually paired with the coupon (small vertical gap) */}
      <div>
        <label className="text-xs text-[#6B6B6B] mb-1.5 block">Gift card</label>
        {hasGc ? (
          <div
            className="flex items-center justify-between px-3 py-2.5 border border-[#B5D0A0] bg-[#F1F7EC] text-sm"
            data-testid="giftcard-applied"
          >
            <span className="text-[#3E6A1E] leading-tight">
              <strong>{totals.giftcard_code}</strong> aplicado —{" "}
              −{formatBRL(totals.giftcard_used)}
            </span>
            <button
              type="button"
              onClick={removeAll}
              disabled={busy}
              className="text-xs text-[#6B6B6B] hover:text-[#1A1A1A] underline underline-offset-4"
              data-testid="giftcard-remove-btn"
            >
              Remover
            </button>
          </div>
        ) : (
          <form onSubmit={applyGc} className="flex gap-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Inserir o código aqui"
              value={gcInput}
              onChange={(e) => setGcInput(e.target.value.toUpperCase())}
              disabled={hasCoupon || busy}
              data-testid="giftcard-input"
              style={{ textTransform: "uppercase" }}
            />
            <button
              type="submit"
              disabled={hasCoupon || busy || !gcInput.trim()}
              className="px-4 text-xs uppercase tracking-wider border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              data-testid="giftcard-apply-btn"
              style={{ width: "22%" }}
            >
              Aplicar
            </button>
          </form>
        )}
        {gcError && (
          <p className="text-xs text-[#A8503B] mt-1.5" data-testid="giftcard-error">
            {gcError}
          </p>
        )}
        {hasCoupon && !hasGc && (
          <p className="text-xs text-[#A8A8A8] mt-1.5">
            Remova o cupom para usar um gift card.
          </p>
        )}
      </div>
    </div>
  );
}
